/*
 * Copyright (c) 2016 Omnigon Communications, LLC. All rights reserved.
 *
 * This software is the confidential and proprietary information of Omnigon Communications, LLC
 * ("Confidential Information"). You shall not disclose such Confidential Information and shall access and use it only
 * in accordance with the terms of the license agreement you entered into with Omnigon Communications, LLC, its
 * subsidiaries, affiliates or authorized licensee. Unless required by applicable law or agreed to in writing, this
 * Confidential Information is provided on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 * express or implied. See the license agreement for the specific language governing permissions and limitations.
 */

import can from 'can/util/util';
import Component from 'can/component/component';
import template from './leaderboard-mini.stache';
import DomEventsManager from 'scripts/dom-events-manager';
import ViewModel from './leaderboard-mini.model';
import FeedService from 'scripts/services/base-feed-service';
import dateTime from 'scripts/utils/date-time';
import compare from 'vendors/compare-property/compare-property';

function getStandingsSorter() {
    return compare.properties({
        'position.value': 1,
        'toPar.value': -1,
        teeTime: 1,
    });
}

Component.extend({
    tag: 'leaderboard-mini',
    template: template,
    viewModel: ViewModel,
    events: {
        inserted: function() {
            this.standingsSorter = getStandingsSorter();
            this.fetchLeaderboard();
            DomEventsManager.subscribe(this);
        },
        sortStandings: function(standings) {
            return _(standings || [])
                .thru((list) => {
                    list.sort(this.standingsSorter);
                    return list;
                })
                .thru((list) => list.slice(0, 5))
                .value();
        },
        sortFavorites: function(standings) {
            return _(standings || [])
                .thru((list) => {
                    list.sort(this.standingsSorter);
                    return list;
                })
                .value();
        },
        fetchLeaderboard: function() {
            let leaderboardFeedUrl =
                this.options.leaderboardFeedUrl ||
                window.globalSettings.feeds.smt.leaderboardUrl;

            this.leaderboardFeedService = new FeedService({
                feedUrl: leaderboardFeedUrl,
                fromCache: false,
            });
            this.leaderboardFeedService
                .on('fetching', (ev, value) =>
                    this.viewModel.attr('fetching', value),
                )
                .on('data', (ev, data) => {
                    const standings = this.sortStandings(
                        _.castArray(_.get(data, 'standings', [])).map(
                            (standing) => {
                                standing.teeTime = dateTime.getTimeDiffFromNightInStr(
                                    _.get(standing, 'holesThroughDate'),
                                );
                                standing.hasTeeTime = !_.isNil(
                                    standing.teeTime,
                                );
                                standing.noTeeTime = !standing.hasTeeTime;
                                return standing;
                            },
                        ),
                    );

                    const previousStandings = this.viewModel.attr('standings');
                    const newStandingsMap = {};
                    const previousStandingsMap = {};
                    const removedIndexes = [];

                    if (
                        this.viewModel.attr('short') ||
                        this.viewModel.attr('isLive')
                    ) {
                        let favorites = [];
                        let favoriteIds = this.viewModel.getFavoritesList();
                        _.forEach(_.get(data, 'standings', []), (standing) => {
                            let playerId = _.get(standing, 'player.identifier');
                            if (favoriteIds.indexOf(playerId) != -1) {
                                favorites.push(standing);
                            }
                        });
                        let sortedFavorites = this.sortStandings(favorites);
                        this.viewModel.attr('favorites', sortedFavorites);
                    }

                    standings.forEach((standing) => {
                        newStandingsMap[standing.player.identifier] = standing;
                        standing.isCut =
                            ['WD', 'MC', 'DQ', 'DNS'].indexOf(
                                _.get(standing, 'position.displayValue'),
                            ) > -1;
                    });

                    can.batch.start();
                    if (!previousStandings) {
                        this.viewModel.attr(
                            'standings',
                            this.sortStandings(standings),
                        );
                    } else {
                        previousStandings.forEach((standing, index) => {
                            const playerId = _.get(
                                standing,
                                'player.identifier',
                            );

                            if (newStandingsMap[playerId]) {
                                previousStandingsMap[playerId] = standing;
                            } else {
                                removedIndexes.push(index);
                            }
                        });

                        _.reduce(
                            removedIndexes,
                            (accum, index) => {
                                previousStandings.splice(index - accum, 1);
                                return accum + 1;
                            },
                            0,
                        );

                        _.forEach(standings, (standing, index) => {
                            const playerId = _.get(
                                standing,
                                'player.identifier',
                            );
                            const previousStanding =
                                previousStandingsMap[playerId];

                            if (previousStanding) {
                                _.each(standing, (value, key) => {
                                    previousStanding.attr(key, value);
                                });
                                _.each(previousStanding, (value, key) => {
                                    if (standing[key] === undefined) {
                                        previousStanding.removeAttr(key);
                                    }
                                });
                            } else {
                                previousStandings.push(standing);
                            }
                        });

                        this.viewModel.attr(
                            'standings',
                            this.sortStandings(previousStandings),
                        );
                    }
                    can.batch.stop();

                    if (
                        !this.viewModel.attr('short') &&
                        !this.viewModel.attr('isLive')
                    ) {
                        // move to the next tick after render
                        setTimeout(() => {
                            const activePlayerId = this.viewModel.attr(
                                'activeId',
                            );
                            const playerId = this.viewModel.attr(
                                'standings.0.player.identifier',
                            );

                            if (!this.viewModel.attr('autoOpened')) {
                                if (!activePlayerId) {
                                    this.viewModel.attr('activeId', playerId);
                                    this.viewModel.attr('autoOpened', true);
                                }
                            } else {
                                if (!newStandingsMap[activePlayerId]) {
                                    this.viewModel.attr('activeId', '');
                                }
                            }
                        }, 0);
                    }
                });
            this.leaderboardFeedService.fetch();
        },
    },
});
